import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { api, unwrap } from "../api/client";

const DEVICES_KEY = ["devices"] as const;

const utc = (t: string) => `${t.slice(0, 16).replace("T", " ")} UTC`;

/** Device credentials for SFTP and FTPS upload (design §6.1.2, §6.1.3). */
export function DevicesPage() {
  const qc = useQueryClient();
  const devices = useQuery({
    queryKey: DEVICES_KEY,
    queryFn: () => unwrap(api.GET("/api/devices")),
  });
  const revoke = useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.POST("/api/devices/{id}/revoke", { params: { path: { id } } }),
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: DEVICES_KEY }),
  });
  const { sftp, ftps, ftp, https } = devices.data ?? {};
  const [issued, setIssued] = useState<{ name: string; token: string } | null>(
    null,
  );
  const reissue = useMutation({
    mutationFn: (d: { id: string; name: string }) =>
      unwrap(
        api.POST("/api/devices/{id}/token", { params: { path: { id: d.id } } }),
      ).then((t) => ({ name: d.name, token: t.api_token })),
    onSuccess: (t) => {
      setIssued(t);
      void qc.invalidateQueries({ queryKey: DEVICES_KEY });
    },
  });
  const [now] = useState(() => Date.now());

  return (
    <Stack spacing={3} component="section" aria-label="Devices">
      <Typography variant="h5" component="h2">
        Devices
      </Typography>
      <Typography color="text.secondary">
        Recording devices and docking stations upload over SFTP with their own
        SSH key, over FTPS with a password AVGV generates, or over HTTPS with an
        API token AVGV generates, into one team&apos;s library. A device can
        only add files: it can&apos;t read, change or delete anything, or sign
        in to AVGV.
      </Typography>
      {devices.isSuccess &&
        (sftp ? (
          <Alert severity="info">
            SFTP is at <strong>{sftp.address}</strong>. Configure each device
            with its name as the username and its key, and check that the
            server&apos;s host key fingerprint is{" "}
            <code aria-label="Host key fingerprint">
              {sftp.host_key_fingerprint}
            </code>
            .
          </Alert>
        ) : (
          <Alert severity="warning">
            SFTP is off on this installation. Devices can upload once the server
            is started with an SFTP address (see the user guide).
          </Alert>
        ))}
      {devices.isSuccess &&
        (ftps ? (
          <Alert severity="info">
            FTPS is at <strong>{ftps.address}</strong> (explicit TLS, passive
            mode). Configure each device with its name as the username and its
            password, and check that the server&apos;s certificate SHA-256 is{" "}
            <code aria-label="Certificate fingerprint">
              {ftps.certificate_sha256}
            </code>
            .
          </Alert>
        ) : (
          <Alert severity="warning">
            FTPS is off on this installation. Devices can upload once the server
            is started with an FTPS address (see the user guide).
          </Alert>
        ))}
      {https && (
        <Alert severity="info">
          HTTPS uploads (TUS 1.0) are at <strong>{https.address}</strong>.
          Configure each device with its API token, sent as{" "}
          <code>Authorization: Bearer &lt;token&gt;</code>, and check that the
          server&apos;s certificate SHA-256 is{" "}
          <code aria-label="HTTPS certificate fingerprint">
            {https.certificate_sha256}
          </code>
          .
        </Alert>
      )}
      {issued && (
        <Alert severity="success" onClose={() => setIssued(null)}>
          New API token for {issued.name}:{" "}
          <code aria-label="API token" style={{ overflowWrap: "anywhere" }}>
            {issued.token}
          </code>
          . Enter it in the device now: it isn&apos;t shown again, and the
          previous token no longer works.
        </Alert>
      )}
      {reissue.isError && (
        <Alert severity="error">{reissue.error.message}</Alert>
      )}
      {ftp && (
        <Alert severity="error">
          Plain FTP is on at <strong>{ftp.address}</strong> for{" "}
          {ftp.allowed_ips.join(", ")}: device passwords and recordings cross
          the network unencrypted. Use it only on an isolated network.
        </Alert>
      )}
      <AddDevice />
      {devices.isError && (
        <Alert severity="error">{devices.error.message}</Alert>
      )}
      {revoke.isError && <Alert severity="error">{revoke.error.message}</Alert>}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label="Device list">
          <TableHead>
            <TableRow>
              <TableCell>Name (username)</TableCell>
              <TableCell>Team library</TableCell>
              <TableCell>Unit</TableCell>
              <TableCell>Signs in with</TableCell>
              <TableCell>Allowed from</TableCell>
              <TableCell>Last used</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {devices.data?.devices.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>No devices yet.</TableCell>
              </TableRow>
            )}
            {devices.data?.devices.map((d) => {
              const expired =
                d.expires_at != null && Date.parse(d.expires_at) <= now;
              return (
                <TableRow key={d.id}>
                  <TableCell>{d.name}</TableCell>
                  <TableCell>{d.team_name}</TableCell>
                  <TableCell>{d.unit ?? "Its folder"}</TableCell>
                  <TableCell>
                    {d.key_fingerprint && (
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace", fontSize: "0.8em" }}
                      >
                        SSH key {d.key_fingerprint}
                      </Typography>
                    )}
                    {d.ftp_password && (
                      <Typography variant="body2">FTPS password</Typography>
                    )}
                    {d.api_token && (
                      <Typography variant="body2">HTTPS token</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {d.allowed_ips.length
                      ? d.allowed_ips.join(", ")
                      : "Anywhere"}
                  </TableCell>
                  <TableCell>
                    {d.last_used_at ? utc(d.last_used_at) : "Never"}
                  </TableCell>
                  <TableCell>
                    {d.revoked_at ? (
                      <Chip size="small" color="error" label="Revoked" />
                    ) : expired ? (
                      <Chip size="small" label="Expired" />
                    ) : (
                      <Chip
                        size="small"
                        color="success"
                        label={
                          d.expires_at ? `Until ${utc(d.expires_at)}` : "Active"
                        }
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {!d.revoked_at && (
                      <Button
                        size="small"
                        disabled={reissue.isPending}
                        onClick={() => {
                          if (
                            !d.api_token ||
                            window.confirm(
                              `Make a new API token for ${d.name}? The current one stops working.`,
                            )
                          )
                            reissue.mutate({ id: d.id, name: d.name });
                        }}
                      >
                        {d.api_token ? "New token" : "Add token"}
                      </Button>
                    )}
                    {!d.revoked_at && (
                      <Button
                        size="small"
                        color="error"
                        disabled={revoke.isPending}
                        onClick={() => {
                          if (
                            window.confirm(
                              `Revoke ${d.name}? It can't upload again; add it anew with a new key if needed.`,
                            )
                          )
                            revoke.mutate(d.id);
                        }}
                      >
                        Revoke
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

function AddDevice() {
  const qc = useQueryClient();
  const teams = useQuery({
    queryKey: ["teams"],
    queryFn: () => unwrap(api.GET("/api/teams")),
  });
  const [name, setName] = useState("");
  const [library, setLibrary] = useState("");
  const [unit, setUnit] = useState("");
  const [key, setKey] = useState("");
  const [ftpPassword, setFtpPassword] = useState(false);
  const [apiToken, setApiToken] = useState(false);
  const [created, setCreated] = useState<{
    name: string;
    password: string | null;
    token: string | null;
  } | null>(null);
  const [ips, setIps] = useState("");
  const [expires, setExpires] = useState("");
  const add = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/api/devices", {
          body: {
            name,
            library_id: library,
            unit: unit.trim() || null,
            public_key: key.trim() || null,
            ftp_password: ftpPassword,
            api_token: apiToken,
            allowed_ips: ips
              .split(/[\s,]+/)
              .map((s) => s.trim())
              .filter(Boolean),
            expires_at: expires ? `${expires}:00Z` : null,
          },
        }),
      ),
    onSuccess: (r) => {
      setCreated(
        r.ftp_password || r.api_token
          ? {
              name: r.device.name,
              password: r.ftp_password ?? null,
              token: r.api_token ?? null,
            }
          : null,
      );
      setApiToken(false);
      setName("");
      setUnit("");
      setKey("");
      setFtpPassword(false);
      setIps("");
      setExpires("");
      void qc.invalidateQueries({ queryKey: DEVICES_KEY });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    add.mutate();
  };

  return (
    <Paper component="form" variant="outlined" onSubmit={submit} sx={{ p: 2 }}>
      <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
        Add a device
      </Typography>
      <Stack spacing={2}>
        <Stack direction="row" sx={{ gap: 2, flexWrap: "wrap" }}>
          <TextField
            label="Name"
            helperText="Also its username, e.g. van7-dock"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <FormControl sx={{ minWidth: 220 }} required>
            <InputLabel id="device-library">Team library</InputLabel>
            <Select
              labelId="device-library"
              label="Team library"
              value={library}
              onChange={(e) => setLibrary(e.target.value)}
            >
              {teams.data?.map((t) => (
                <MenuItem key={t.id} value={t.library_collection_id}>
                  {t.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Unit"
            helperText="Optional; else the folder it uploads into"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
          />
        </Stack>
        <TextField
          label="Public key (SFTP)"
          helperText="The device's OpenSSH public key (Ed25519 or ECDSA), e.g. ssh-ed25519 AAAA…"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          multiline
          minRows={2}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={ftpPassword}
              onChange={(e) => setFtpPassword(e.target.checked)}
            />
          }
          label="Generate a password for FTPS (needs Allowed from)"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={apiToken}
              onChange={(e) => setApiToken(e.target.checked)}
            />
          }
          label="Generate an API token for HTTPS uploads"
        />
        <Stack direction="row" sx={{ gap: 2, flexWrap: "wrap" }}>
          <TextField
            label="Allowed from"
            helperText="Addresses or ranges, e.g. 192.168.10.0/24; empty: anywhere"
            value={ips}
            onChange={(e) => setIps(e.target.value)}
            sx={{ minWidth: 320 }}
          />
          <TextField
            label="Expires (UTC)"
            type="datetime-local"
            helperText="Optional"
            value={expires}
            onChange={(e) => setExpires(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Stack>
        {add.isError && <Alert severity="error">{add.error.message}</Alert>}
        {created && (
          <Alert severity="success" onClose={() => setCreated(null)}>
            {created.password && (
              <>
                FTPS password for {created.name}:{" "}
                <code aria-label="FTPS password">{created.password}</code>.{" "}
              </>
            )}
            {created.token && (
              <>
                API token for {created.name}:{" "}
                <code
                  aria-label="API token"
                  style={{ overflowWrap: "anywhere" }}
                >
                  {created.token}
                </code>
                .{" "}
              </>
            )}
            Enter it in the device now: it isn&apos;t shown again. A lost
            password means adding the device anew; a lost token, a new token.
          </Alert>
        )}
        <div>
          <Button
            type="submit"
            variant="contained"
            disabled={
              add.isPending ||
              !library ||
              (!key.trim() && !ftpPassword && !apiToken) ||
              (ftpPassword && !ips.trim())
            }
          >
            Add device
          </Button>
        </div>
      </Stack>
    </Paper>
  );
}
